import { useState } from 'react';
import {
  OutlinedInput, InputLabel, Checkbox, FormControlLabel,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { AddList, List } from 'lib/types';
import { useTeams } from 'lib/contexts/teamsContext';
import { userIsAdmin, userIsProOrAdmin, userIsPaidTeamMember } from 'lib/helpers';
import { MobileTooltip } from 'components/MobileTooltip';
import { User } from 'lib/types/users';
import { useSelectedUser } from 'components/UserSearch/useSelectedUser';
import { UserSearch } from 'components/UserSearch';
import { toSlug } from 'lib/utils/filterUtils';
import Link from 'next/link';
import { TeamSelect } from './TeamSelect';
import { ListSelect } from './ListSelect';

const initialListState: Partial<List> = {
  name: '',
  teamId: undefined,
  isPrivate: true,
  allowCreatorAdd: false,
};

interface Props {
  lists: List[];
  onSave: (list: List) => void;
  onCancel: () => void;
  list?: List;
}

export const AddEditList = ({
  lists, list, onSave, onCancel,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [_list, setList] = useState(list || initialListState);
  const [copyFromListId, setCopyFromListId] = useState<number | undefined>();

  const teams = useTeams();
  /* teams that user's a member of */
  const myTeams = teams?.filter(
    (t) => t.members.find((m) => m.userId === currentUser?.id),
  ) || [];

  const [users, setUsers] = useState<User[]>([]);
  const { onHandleSelectUser } = useSelectedUser(null);

  const onSelectAccount = (value: User[]) => {
    setUsers(value.filter((u) => !!u.id));
    const toAdd = value.filter((u) => !u.id);
    toAdd.map(async (u) => onHandleSelectUser(u, currentUser?.hasPaidTeam));
  };

  const saveList = async () => {
    if (!currentUser) return;

    try {
      let result: List;
      if (list?.id) {
        result = await API.editList({
          ..._list as List,
        });
        enqueueSnackbar(`${_list.name} has been updated.`, {
          variant: 'success',
        });
      } else {
        result = await API.addList({
          ..._list,
          ownerId: _list.teamId ? undefined : currentUser.id,
          copyFromListId,
          users,
        } as AddList);
        const action = () => (
          <button type="button" className="btn">
            <Link href={`/creators?list=${toSlug(result.name, result.publicId)}`}>
              View list
            </Link>
          </button>
        );
        enqueueSnackbar(`${_list.name} has been created.`, {
          variant: 'success',
          action,
        });
      }
      onSave(result);
      onCancel();
    } catch (error) {
      enqueueSnackbar(`Error saving list: ${error}`, {
        variant: 'error',
      });
    }
  };

  const changeTeam = (id?: number) => {
    setList({ ..._list, teamId: id });
  };

  return (
    <div>
      <h1 className="text-lg font-semibold mb-4">
        {list ? 'Edit List' : 'Create a New List'}
      </h1>
      <div className="mb-4">
        <InputLabel id="list-name-label" className="font-semibold mb-2">Name</InputLabel>
        <OutlinedInput
          type="text"
          id="name"
          name="name"
          placeholder="Name"
          value={_list.name}
          className="w-full"
          size="small"
          onChange={
            (e) => {
              setList({ ..._list, name: e.target.value });
            }
          }
        />
      </div>
      <div className="mb-4">
        <FormControlLabel
          control={(
            <Checkbox
              checked={_list.isPrivate}
              onChange={() => setList({ ..._list, isPrivate: !_list.isPrivate })}
              name="isPrivate"
              color="primary"
              disabled={!userIsProOrAdmin(currentUser) && !userIsPaidTeamMember(currentUser)}
            />
          )}
          label="Private"
        />
        <MobileTooltip
          placement="top"
          arrow
          title="Lists are automatically private, but you can make them public if you want to share them with others."
        >
          <Info color="secondary" />
        </MobileTooltip>
      </div>
      {userIsAdmin(currentUser) && (
        <div className="mb-4">
          <FormControlLabel
            control={(
              <Checkbox
                checked={_list.allowCreatorAdd}
                onChange={() => setList({ ..._list, allowCreatorAdd: !_list.allowCreatorAdd })}
                name="allowCreatorAdd"
                color="primary"
                disabled={!userIsProOrAdmin(currentUser) && !userIsPaidTeamMember(currentUser)}
              />
            )}
            label="Allow creators to add themselves to this list"
          />
          <MobileTooltip
            placement="top"
            arrow
            title="Share this list with creators and allow them to add themselves to it. This is only available for Pro and Team users."
          >
            <Info color="secondary" />
          </MobileTooltip>
        </div>
      )}
      {!list && myTeams.length >= 1 && (
        <div className="mb-4">
          <TeamSelect
            teams={myTeams}
            selected={_list.teamId}
            onChange={changeTeam}
          />
        </div>
      )}
      {!list?.id && (
        <div className="mb-4">
          <UserSearch
            label="Add creators to your new list"
            onChange={(_, value) => onSelectAccount(value)}
            isCreatableSearch
            multiple
            value={users}
          />
        </div>
      )}
      {userIsAdmin(currentUser) && !list && lists.length > 0 && (
        <ListSelect
          label="Copy creators from another list (admin only)"
          lists={lists}
          selected={copyFromListId}
          onChange={setCopyFromListId}
        />
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn-primary-outlined" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-primary" onClick={saveList} disabled={!_list.name?.trim()}>Save</button>
      </div>
    </div>
  );
};
